import type { QueryClient } from '@tanstack/react-query';
import { lazyMapFor } from '@/common/utils/lazyMapFor';

type ClaimState = {
  readonly ids: readonly number[];
  superseded: boolean;
  released: boolean;
  readonly teardowns: (() => void)[];
};

export interface RowReorderClaim {
  isCurrent: () => boolean;
  onSupersede: (teardown: () => void) => void;
  release: () => void;
}

const ownersByClient = new WeakMap<QueryClient, Map<number, ClaimState>>();

const dropOwnership = (owners: Map<number, ClaimState>, state: ClaimState): void => {
  for (const id of state.ids) {
    if (owners.get(id) === state) owners.delete(id);
  }
};

const supersede = (owners: Map<number, ClaimState>, state: ClaimState): void => {
  if (state.superseded || state.released) return;
  state.superseded = true;
  dropOwnership(owners, state);
  for (const teardown of state.teardowns) teardown();
};

export const claimRows = (queryClient: QueryClient, ids: readonly number[]): RowReorderClaim => {
  const owners = lazyMapFor(ownersByClient, queryClient);
  for (const id of ids) {
    const previous = owners.get(id);
    if (previous) supersede(owners, previous);
  }

  const state: ClaimState = { ids, superseded: false, released: false, teardowns: [] };
  for (const id of ids) owners.set(id, state);

  return {
    isCurrent: () => !state.superseded && !state.released,
    onSupersede: (teardown) => {
      state.teardowns.push(teardown);
    },
    release: () => {
      if (state.superseded || state.released) return;
      state.released = true;
      dropOwnership(owners, state);
    },
  };
};
